import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface PortalApiConfig {
  retailer_lookup_endpoint: string;
  bot_trigger_endpoint: string;
  auth_method: 'bearer' | 'api_key' | 'basic' | 'custom';
  auth_config: {
    token?: string;
    api_key?: string;
    username?: string;
    password?: string;
    headers?: Record<string, string>;
  };
  webhook_url?: string;
  timeout?: number;
}

interface ConnectedPortal {
  id: string;
  region: string;
  supplier: string;
  logo: string;
  status: string;
  lastSync: string;
  billsCollected: number;
  apiConfig?: PortalApiConfig;
}

interface PortalConfigModalProps {
  portal: ConnectedPortal;
  onClose: () => void;
  onConfigSaved: (portal: ConnectedPortal) => void;
}

export const PortalConfigModal: React.FC<PortalConfigModalProps> = ({ 
  portal, 
  onClose, 
  onConfigSaved 
}) => {
  const { toast } = useToast();
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  
  const [config, setConfig] = useState<PortalApiConfig>({
    retailer_lookup_endpoint: portal.apiConfig?.retailer_lookup_endpoint || '',
    bot_trigger_endpoint: portal.apiConfig?.bot_trigger_endpoint || '',
    auth_method: portal.apiConfig?.auth_method || 'bearer',
    auth_config: {
      token: portal.apiConfig?.auth_config?.token || '',
      api_key: portal.apiConfig?.auth_config?.api_key || '',
      username: portal.apiConfig?.auth_config?.username || '',
      password: portal.apiConfig?.auth_config?.password || '',
      headers: portal.apiConfig?.auth_config?.headers || {}
    },
    webhook_url: portal.apiConfig?.webhook_url || '',
    timeout: portal.apiConfig?.timeout || 30000
  });

  const [customHeaders, setCustomHeaders] = useState(
    Object.entries(config.auth_config.headers || {}).map(([key, value]) => ({ key, value }))
  );

  const handleAuthMethodChange = (method: PortalApiConfig['auth_method']) => {
    setConfig(prev => ({
      ...prev,
      auth_method: method,
      auth_config: {
        ...prev.auth_config,
        token: method === 'bearer' ? prev.auth_config.token : '',
        api_key: method === 'api_key' ? prev.auth_config.api_key : '',
        username: method === 'basic' ? prev.auth_config.username : '',
        password: method === 'basic' ? prev.auth_config.password : ''
      }
    }));
  };

  const updateCustomHeaders = () => {
    const headers: Record<string, string> = {};
    customHeaders.forEach(({ key, value }) => {
      if (key && value) headers[key] = value;
    });
    setConfig(prev => ({
      ...prev,
      auth_config: { ...prev.auth_config, headers }
    }));
  };

  const addCustomHeader = () => {
    setCustomHeaders(prev => [...prev, { key: '', value: '' }]);
  };

  const removeCustomHeader = (index: number) => {
    setCustomHeaders(prev => prev.filter((_, i) => i !== index));
  };

  const testConnection = async () => {
    if (!config.retailer_lookup_endpoint || !config.bot_trigger_endpoint) {
      toast({
        title: "Error",
        description: "Please enter both API endpoints to test",
        variant: "destructive",
      });
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      updateCustomHeaders(); // Update headers before testing
      
      const response = await supabase.functions.invoke('portal-integration-orchestrator', {
        body: {
          customerId: 'test',
          portalConfig: {
            id: portal.id,
            supplier_id: portal.id,
            supplier_name: portal.supplier,
            region: portal.region,
            credentials: { test: true },
            retailer_lookup_endpoint: config.retailer_lookup_endpoint,
            bot_trigger_endpoint: config.bot_trigger_endpoint,
            auth_method: config.auth_method,
            auth_config: config.auth_config,
            webhook_url: config.webhook_url,
            timeout: config.timeout
          },
          testMode: true
        }
      });

      if (response.error) {
        setTestResult({ success: false, message: response.error.message });
      } else {
        setTestResult({ 
          success: true, 
          message: 'Two-step API workflow test successful!' 
        });
      }
    } catch (error) {
      setTestResult({ 
        success: false, 
        message: error instanceof Error ? error.message : 'Connection failed' 
      });
    } finally {
      setTesting(false);
    }
  };

  const saveConfiguration = async () => {
    if (!config.retailer_lookup_endpoint || !config.bot_trigger_endpoint) {
      toast({
        title: "Error",
        description: "Both API endpoints are required",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);

    try {
      updateCustomHeaders(); // Update headers before saving
      
      // Save to customer_sources table with portal configuration  
      const { error: configError } = await supabase
        .from('customer_sources')
        .upsert({
          user_id: portal.id,
          customer_id: portal.id,
          source_type: 'portal',
          status: 'inactive',
          configuration: {
            supplier_name: portal.supplier,
            region: portal.region,
            retailer_lookup_endpoint: config.retailer_lookup_endpoint,
            bot_trigger_endpoint: config.bot_trigger_endpoint,
            auth_method: config.auth_method,
            auth_config: config.auth_config,
            webhook_url: config.webhook_url,
            timeout: config.timeout
          },
          sync_frequency: 'daily'
        });

      if (configError) throw configError;

      const updatedPortal = {
        ...portal,
        apiConfig: config
      };

      onConfigSaved(updatedPortal);
      
      toast({
        title: "Configuration Saved",
        description: "Portal API configuration saved successfully!",
      });
      
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save configuration",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full m-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <img 
              src={portal.logo} 
              alt={portal.supplier}
              className="w-8 h-8 rounded"
              onError={(e) => {(e.target as HTMLImageElement).src = '/placeholder-logo.png'}}
            />
            <div>
              <h2 className="text-xl font-semibold text-foreground">Configure API Integration</h2>
              <p className="text-sm text-muted-foreground">{portal.supplier} - {portal.region}</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* API Endpoints */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Retailer Lookup Endpoint *
              </label>
              <input
                type="url"
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring bg-background text-foreground"
                value={config.retailer_lookup_endpoint}
                onChange={(e) => setConfig(prev => ({ ...prev, retailer_lookup_endpoint: e.target.value }))}
                placeholder="https://api.example.com/retailer/lookup"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Step 1: API endpoint to identify the utility supplier
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Bot Trigger Endpoint *
              </label>
              <input
                type="url"
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring bg-background text-foreground"
                value={config.bot_trigger_endpoint}
                onChange={(e) => setConfig(prev => ({ ...prev, bot_trigger_endpoint: e.target.value }))}
                placeholder="https://api.example.com/bot/trigger"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Step 2: API endpoint to trigger bill collection bot
              </p>
            </div>
          </div>

          {/* Authentication Method */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Authentication Method
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'bearer', label: 'Bearer Token' },
                { value: 'api_key', label: 'API Key' },
                { value: 'basic', label: 'Basic Auth' },
                { value: 'custom', label: 'Custom Headers' }
              ].map(method => (
                <button
                  key={method.value}
                  type="button"
                  onClick={() => handleAuthMethodChange(method.value as PortalApiConfig['auth_method'])}
                  className={`p-3 border rounded-lg text-sm font-medium transition-colors ${
                    config.auth_method === method.value
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border text-foreground hover:bg-muted'
                  }`}
                >
                  {method.label}
                </button>
              ))}
            </div>
          </div>

          {/* Authentication Configuration */}
          <div className="space-y-4">
            {config.auth_method === 'bearer' && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Bearer Token *
                </label>
                <input
                  type="password"
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring bg-background text-foreground"
                  value={config.auth_config.token}
                  onChange={(e) => setConfig(prev => ({
                    ...prev,
                    auth_config: { ...prev.auth_config, token: e.target.value }
                  }))}
                  placeholder="Enter bearer token"
                />
              </div>
            )}

            {config.auth_method === 'api_key' && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  API Key *
                </label>
                <input
                  type="password"
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring bg-background text-foreground"
                  value={config.auth_config.api_key}
                  onChange={(e) => setConfig(prev => ({
                    ...prev,
                    auth_config: { ...prev.auth_config, api_key: e.target.value }
                  }))}
                  placeholder="Enter API key"
                />
              </div>
            )}

            {config.auth_method === 'basic' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Username *
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring bg-background text-foreground"
                    value={config.auth_config.username}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      auth_config: { ...prev.auth_config, username: e.target.value }
                    }))}
                    placeholder="Username"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Password *
                  </label>
                  <input
                    type="password"
                    className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring bg-background text-foreground"
                    value={config.auth_config.password}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      auth_config: { ...prev.auth_config, password: e.target.value }
                    }))}
                    placeholder="Password"
                  />
                </div>
              </div>
            )}

            {config.auth_method === 'custom' && (
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-foreground">
                    Custom Headers
                  </label>
                  <Button type="button" size="sm" variant="outline" onClick={addCustomHeader}>
                    + Add Header
                  </Button>
                </div>
                <div className="space-y-2">
                  {customHeaders.map((header, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        className="flex-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring bg-background text-foreground"
                        value={header.key}
                        onChange={(e) => {
                          const newHeaders = [...customHeaders];
                          newHeaders[index] = { ...header, key: e.target.value };
                          setCustomHeaders(newHeaders);
                        }}
                        placeholder="Header name"
                      />
                      <input
                        type="text"
                        className="flex-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring bg-background text-foreground"
                        value={header.value}
                        onChange={(e) => {
                          const newHeaders = [...customHeaders];
                          newHeaders[index] = { ...header, value: e.target.value };
                          setCustomHeaders(newHeaders);
                        }}
                        placeholder="Header value"
                      />
                      <Button 
                        type="button" 
                        size="sm" 
                        variant="outline" 
                        onClick={() => removeCustomHeader(index)}
                      >
                        ✕
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Optional Settings */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Webhook URL (Optional)
              </label>
              <input
                type="url"
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring bg-background text-foreground"
                value={config.webhook_url}
                onChange={(e) => setConfig(prev => ({ ...prev, webhook_url: e.target.value }))}
                placeholder="https://your-app.com/webhook"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Timeout (ms)
              </label>
              <input
                type="number"
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring bg-background text-foreground"
                value={config.timeout}
                onChange={(e) => setConfig(prev => ({ ...prev, timeout: parseInt(e.target.value) || 30000 }))}
                min="5000"
                max="300000"
              />
            </div>
          </div>

          {/* Test Result */}
          {testResult && (
            <div className={`p-4 rounded-lg border ${
              testResult.success 
                ? 'bg-green-500/5 border-green-500/20' 
                : 'bg-red-500/5 border-red-500/20'
            }`}>
              <div className={`flex items-center gap-2 text-sm font-medium ${
                testResult.success ? 'text-green-600' : 'text-red-600'
              }`}>
                {testResult.success ? '✅' : '❌'} Connection Test Result
              </div>
              <p className={`text-sm mt-1 ${
                testResult.success ? 'text-green-600/80' : 'text-red-600/80'
              }`}>
                {testResult.message}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-border bg-muted/30">
          <div className="text-sm text-muted-foreground">
            🔒 API credentials are encrypted and stored securely
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={testConnection} disabled={testing || !config.retailer_lookup_endpoint || !config.bot_trigger_endpoint}>
              {testing ? 'Testing...' : '🧪 Test 2-Step Workflow'}
            </Button>
            <Button onClick={onClose} variant="outline">
              Cancel
            </Button>
            <Button onClick={saveConfiguration} disabled={saving || !config.retailer_lookup_endpoint || !config.bot_trigger_endpoint}>
              {saving ? 'Saving...' : '💾 Save Configuration'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};